import { useEffect, useMemo, useState } from 'react'
import { IconChevronLeft, IconChevronRight } from '../icons.js'
import { подходитЗапросу } from '../ui/search.js'
import type { NodeDefinitionView } from './definitions.js'

export interface PaletteProps {
  definitions: readonly NodeDefinitionView[]
  onPick: (definition: NodeDefinitionView) => void
  /** Свёрнута ли палитра (FR-ADM-044). Не задано — всегда развёрнута. */
  collapsed?: boolean
  /** Сворачивание. Не задано — сворачивать нечем, и кнопки нет. */
  onCollapsedChange?: (collapsed: boolean) => void
}

/** Человеческие названия групп: в графе они латиницей, на экране — нет. */
const GROUP_NAMES: Readonly<Record<string, string>> = {
  trigger: 'Триггеры',
  flow: 'Поток',
  data: 'Данные',
  integration: 'Интеграции',
  whatsapp: 'WhatsApp',
  ai: 'ИИ',
  utility: 'Утилиты',
}

function groupNameOf(group: string): string {
  return GROUP_NAMES[group] ?? group
}

/**
 * Сочетание для сворачивания. Не Ctrl+K: он занят командной палитрой, и
 * два разных предмета под одним именем «палитра» на одной клавише — это
 * способ гарантированно нажать не то.
 *
 * Ctrl+B — то же, чем сворачивают боковую панель в редакторах кода, откуда
 * человек сюда и приходит. В полях ввода портала он ничего не значит:
 * жирного начертания в них нет.
 */
const КЛАВИША_СВОРАЧИВАНИЯ = 'b'

/**
 * Палитра нод. Список приходит от службы описаниями, а не зашит в редактор
 * (FR-NODE-001): иначе каждая новая нода требовала бы пересборки админки.
 *
 * Поиск идёт и по названию, и по группе — человек ищет то «отправку», то
 * «поток», и заставлять его помнить, в какой группе лежит нужное, значит
 * заставлять пролистывать всё.
 *
 * Сворачивается (FR-ADM-044), потому что холсту при ширине окна в 1600
 * оставалось около 710 пикселей: боковая навигация, палитра и колонка
 * панелей забирали 888, и сценарий из семи узлов приходилось смотреть в
 * масштабе 0,39 — на нём подписи узлов уже не читаются. Палитрой при этом
 * пользуются вспышками: открыл, поставил узел, забыл о ней до следующего.
 */
export function Palette({
  definitions,
  onPick,
  collapsed = false,
  onCollapsedChange,
}: PaletteProps): React.JSX.Element {
  const [query, setQuery] = useState('')

  const свернуть = onCollapsedChange
  useEffect(() => {
    if (свернуть === undefined) return
    function приНажатии(event: KeyboardEvent): void {
      if (event.key.toLowerCase() !== КЛАВИША_СВОРАЧИВАНИЯ) return
      if (!(event.ctrlKey || event.metaKey)) return
      event.preventDefault()
      свернуть?.(!collapsed)
    }
    window.addEventListener('keydown', приНажатии)
    return () => window.removeEventListener('keydown', приНажатии)
  }, [collapsed, свернуть])

  // Сравнение не различает «е» и «ё» (FR-ADM-038): «сче» обязано находить
  // и «счёт», и «счетах». Правило общее с командной палитрой — search.ts.
  const found = useMemo(() => {
    if (query.trim() === '') return definitions
    return definitions.filter((definition) =>
      подходитЗапросу(
        [
          definition.displayName,
          definition.group,
          groupNameOf(definition.group),
          definition.description,
        ].join(' '),
        query,
      ),
    )
  }, [definitions, query])

  const groups = useMemo(() => {
    const byGroup = new Map<string, NodeDefinitionView[]>()
    for (const definition of found) {
      byGroup.set(definition.group, [...(byGroup.get(definition.group) ?? []), definition])
    }
    return [...byGroup.entries()]
  }, [found])

  /*
   * Свёрнутая палитра называет себя словом, а не значком.
   *
   * Полоска со стрелкой не говорит, что за ней: развернуть её один раз
   * из любопытства человек может, а вспомнить через неделю, что узлы
   * добавляются именно оттуда, — уже нет. Поэтому вдоль полосы стоит
   * «УЗЛЫ», и «+» перед ним: не «панель, которую можно открыть», а «место,
   * откуда берут узлы».
   *
   * Кнопка одна на всю полосу: попадать мышью в узкую вертикальную мишень
   * тем труднее, чем она уже, и отдавать под неё только верхние 32 пикселя
   * значит сделать сворачивание односторонним на практике.
   */
  if (collapsed) {
    return (
      <aside className="палитра палитра--свёрнута" aria-label="Палитра нод">
        <button
          type="button"
          className="палитра__корешок"
          onClick={() => свернуть?.(false)}
          title="Развернуть палитру узлов (Ctrl+B)"
          aria-label="Развернуть палитру узлов"
          aria-expanded={false}
        >
          <IconChevronRight />
          <span className="палитра__корешок-подпись">Узлы</span>
        </button>
      </aside>
    )
  }

  return (
    <aside className="палитра" aria-label="Палитра нод">
      <div className="палитра__шапка">
        <input
          type="search"
          placeholder="Найти узел"
          aria-label="Найти узел"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value)
          }}
        />
        {свернуть !== undefined ? (
          <button
            type="button"
            className="палитра__свернуть"
            onClick={() => свернуть(true)}
            title="Свернуть палитру узлов (Ctrl+B)"
            aria-label="Свернуть палитру узлов"
            aria-expanded
          >
            <IconChevronLeft />
          </button>
        ) : null}
      </div>

      {found.length === 0 ? (
        <p className="палитра__пусто">
          Ничего не нашлось. Искать можно и по названию ноды, и по группе — например «отправить»
          или «поток».
        </p>
      ) : (
        groups.map(([group, items]) => (
          <section key={group}>
            <h3>{groupNameOf(group)}</h3>
            <ul>
              {items.map((definition) => (
                <li key={`${definition.type}@${definition.version}`}>
                  <button
                    type="button"
                    onClick={() => {
                      onPick(definition)
                    }}
                    title={definition.description}
                  >
                    {definition.displayName}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </aside>
  )
}
